"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, Row, Col, Badge } from "reactstrap"
import type { CountdownData } from "./staff-dashboard"
import StaffDashboardService from "../../../Service/staffDashboardService"

interface ExhibitionCountdownWidgetProps {
  exhibitionId?: number
  onDataUpdate?: (data: CountdownData) => void
}

const ExhibitionCountdownWidget: React.FC<ExhibitionCountdownWidgetProps> = ({ exhibitionId, onDataUpdate }) => {
  const [countdownData, setCountdownData] = useState<CountdownData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCountdownData = async () => {
    try {
      const data = await StaffDashboardService.getExhibitionCountdown(exhibitionId)
      setCountdownData(data)
      onDataUpdate?.(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCountdownData()

    // Update every second for live countdown
    const interval = setInterval(fetchCountdownData, 1000)

    return () => clearInterval(interval)
  }, [exhibitionId])

  const getStatusColor = (status: string) => {
    switch (status) {
      case "upcoming":
        return "warning"
      case "active":
        return "success"
      case "ended":
        return "secondary"
      default:
        return "primary"
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "upcoming":
        return "fa-clock-o"
      case "active":
        return "fa-play-circle"
      case "ended":
        return "fa-stop-circle"
      default:
        return "fa-calendar"
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  if (loading) {
    return (
      <Card className="exhibition-countdown-widget shadow-lg border-0">
        <CardBody className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-muted">Loading exhibition data...</p>
        </CardBody>
      </Card>
    )
  }

  if (error || !countdownData) {
    return (
      <Card className="exhibition-countdown-widget shadow-lg border-0">
        <CardBody className="text-center py-5">
          <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5 className="text-warning">Unable to Load Exhibition Data</h5>
          <p className="text-muted">{error || "No exhibition data available"}</p>
        </CardBody>
      </Card>
    )
  }

  const { exhibition, countdown } = countdownData

  return (
    <>
      <style>{`
        .exhibition-countdown-widget {
       
          color: white;
         
          overflow: hidden;
        }
        
        .countdown-display {
          background: rgba(190, 167, 114, 0.1);
          border-radius: 0.75rem;
          backdrop-filter: blur(10px);
        }
        
        .countdown-item {
          text-align: center;
          padding: 1rem;
        }
        
        .countdown-number {
          font-size: 2.5rem;
          font-weight: bold;
          line-height: 1;
          text-shadow: 0 2px 4px rgba(68, 60, 11, 0.3);
        }
        
        .countdown-label {
          font-size: 0.875rem;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 0.25rem;
        }
        
        .exhibition-info {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .status-badge {
          font-size: 0.75rem;
          padding: 0.375rem 0.75rem;
          border-radius: 1rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .pulse-animation {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }
        
        @media (max-width: 768px) {
          .countdown-number {
            font-size: 1.75rem;
          }
          .countdown-item {
            padding: 0.75rem 0.5rem;
          }
        }
      `}</style>

      <Card className="exhibition-countdown-widget bg-primary border-0">
        <CardBody className="p-2">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <i className={`fa ${getStatusIcon(countdown.status)} fa-2x me-3`}></i>
              <div>
                <h4 className="mb-0 fw-bold text-white">Current Exhibition</h4>
                <small className="opacity-75 text-white">Live Status Monitor</small>
              </div>
            </div>
            <Badge
              color={getStatusColor(countdown.status)}
              className={`status-badge ${countdown.status === "active" ? "pulse-animation" : ""}`}
            >
              <i className={`fa ${getStatusIcon(countdown.status)} me-1`}></i>
              {countdown.status}
            </Badge>
          </div>

          {/* Exhibition Info */}
          <div className="exhibition-info">
            <h5 className="mb-2 fw-bold text-white">{exhibition.title}</h5>
            <div className="d-flex flex-wrap gap-3 small">
              <div>
                <i className="fa fa-map-marker me-1"></i>
                {exhibition.venue}
              </div>
              <div>
                <i className="fa fa-calendar me-1"></i>
                {formatDate(exhibition.start_date)}
              </div>
              <div>
                <i className="fa fa-calendar-check-o me-1"></i>
                {formatDate(exhibition.end_date)}
              </div>
            </div>
          </div>

          {/* Countdown Display */}
          {countdown.status !== "ended" && countdown.total_seconds > 0 && (
            <>
              <div className="text-center mb-2 text-white">
                <h6 className="mb-0 opacity-90 text-white fw-bold">
                  {countdown.countdown_to === "start" ? "Time Until Exhibition Starts" : "Time Until Exhibition Ends"}
                </h6>
              </div>

              <div className="countdown-display p-1">
                <Row className="g-2">
                  <Col xs="3" className="countdown-item">
                    <div className="countdown-number">{countdown.days}</div>
                    <div className="countdown-label">Days</div>
                  </Col>
                  <Col xs="3" className="countdown-item">
                    <div className="countdown-number">{countdown.hours.toString().padStart(2, "0")}</div>
                    <div className="countdown-label">Hours</div>
                  </Col>
                  <Col xs="3" className="countdown-item">
                    <div className="countdown-number">{countdown.minutes.toString().padStart(2, "0")}</div>
                    <div className="countdown-label">Minutes</div>
                  </Col>
                  <Col xs="3" className="countdown-item">
                    <div className="countdown-number">{countdown.seconds.toString().padStart(2, "0")}</div>
                    <div className="countdown-label">Seconds</div>
                  </Col>
                </Row>
              </div>
            </>
          )}

          {/* Ended State */}
          {countdown.status === "ended" && (
            <div className="text-center py-4">
              <i className="fa fa-check-circle fa-3x mb-3 opacity-75"></i>
              <h5>Exhibition Completed</h5>
              <p className="mb-0 opacity-75">This exhibition has successfully concluded.</p>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

export default ExhibitionCountdownWidget
